import { useCallback, useEffect, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { repositorioCatalogos, type Opcion } from '../../data/repositories/RepositorioCatalogos';
import { guardarFiltros, leerFiltros, olvidarFiltros } from '../utils/filtrosGuardados';

export interface SeleccionCascada {
  clienteId: number | null;
  areaId: number | null;
  nodoId: number | null;
}

const VACIA: SeleccionCascada = { clienteId: null, areaId: null, nodoId: null };

export interface Cascada {
  seleccion: SeleccionCascada;
  clientes: Opcion[];
  areas: Opcion[];
  nodos: Opcion[];
  cargandoClientes: boolean;
  elegirCliente: (id: number | null) => void;
  elegirArea: (id: number | null) => void;
  elegirNodo: (id: number | null) => void;
  limpiar: () => void;
  /** `true` si hay algún filtro puesto. */
  hayFiltros: boolean;
}

/**
 * Cascada de filtros Cliente → Área → Nodo.
 *
 * Elegir un cliente descarta el área y el nodo, y elegir un área descarta el
 * nodo: mantener una selección que ya no pertenece al padre era lo que dejaba
 * pantallas en blanco sin explicación en la versión anterior.
 *
 * Las listas se piden sólo cuando hay un padre seleccionado y quedan en caché,
 * así que volver atrás en la cascada no repite la consulta.
 */
export function useCascadaFiltros(
  inicial: Partial<SeleccionCascada> = {},
  recordarComo?: string,
): Cascada {
  // La selección guardada se lee una sola vez, al montar: leerla en cada
  // render pisaría lo que el usuario acabe de elegir.
  const [seleccion, setSeleccion] = useState<SeleccionCascada>(() => ({
    ...VACIA,
    ...inicial,
    ...(recordarComo ? leerFiltros<SeleccionCascada>(recordarComo) : null),
  }));

  useEffect(() => {
    if (!recordarComo) return;
    guardarFiltros(recordarComo, seleccion);
  }, [recordarComo, seleccion]);

  const consultaClientes = useQuery({
    queryKey: ['catalogo', 'clientes'],
    queryFn: () => repositorioCatalogos.clientes(),
    staleTime: 5 * 60_000,
  });

  const consultaAreas = useQuery({
    queryKey: ['catalogo', 'areas', seleccion.clienteId],
    queryFn: () => repositorioCatalogos.areasDeCliente(seleccion.clienteId),
    enabled: seleccion.clienteId !== null,
    staleTime: 5 * 60_000,
  });

  const consultaNodos = useQuery({
    queryKey: ['catalogo', 'nodos', seleccion.areaId],
    queryFn: () => repositorioCatalogos.nodosDeArea(seleccion.areaId),
    enabled: seleccion.areaId !== null,
    staleTime: 5 * 60_000,
  });

  // Con un solo cliente visible no hay nada que elegir: se fija solo, que es
  // lo que hacía la versión anterior con los usuarios acotados a su cliente.
  const unicoCliente = consultaClientes.data?.length === 1 ? consultaClientes.data[0] : undefined;
  useEffect(() => {
    if (!unicoCliente || seleccion.clienteId !== null) return;
    setSeleccion((s) => ({ ...s, clienteId: unicoCliente.id }));
  }, [unicoCliente, seleccion.clienteId]);

  const elegirCliente = useCallback((clienteId: number | null) => {
    setSeleccion({ clienteId, areaId: null, nodoId: null });
  }, []);

  const elegirArea = useCallback((areaId: number | null) => {
    setSeleccion((s) => ({ ...s, areaId, nodoId: null }));
  }, []);

  const elegirNodo = useCallback((nodoId: number | null) => {
    setSeleccion((s) => ({ ...s, nodoId }));
  }, []);

  const limpiar = useCallback(() => {
    setSeleccion(VACIA);
    // Limpiar es una decisión explícita: no debe volver a aplicarse mañana.
    if (recordarComo) olvidarFiltros(recordarComo);
  }, [recordarComo]);

  return {
    seleccion,
    clientes: consultaClientes.data ?? [],
    areas: consultaAreas.data ?? [],
    nodos: consultaNodos.data ?? [],
    cargandoClientes: consultaClientes.isLoading,
    elegirCliente,
    elegirArea,
    elegirNodo,
    limpiar,
    hayFiltros:
      seleccion.clienteId !== null || seleccion.areaId !== null || seleccion.nodoId !== null,
  };
}
